import { Injectable } from '@angular/core';
import { AppService } from '@app/app.service';
import { Cached, cachedGroup } from '@app/utils/cached';
import { portalError } from '@app/utils/error/portal-error';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class C5VersionCheckerService {
  private _c5Version: Cached<C5Version> | null = null;

  get c5Version(): Cached<C5Version> {
    this._c5Version ??= cachedGroup(
      [this.appService.info],
      ([appInfo]) => {
        return of(this.parseVersion(appInfo.buildInfo.C5ProvisioningVersion));
      },
      {
        id: 'app:c5-version',
      }
    );

    return this._c5Version;
  }

  constructor(private appService: AppService) {}

  atLeast(c5Version: C5Version, version: string): boolean {
    return this.compare(version, c5Version) < 1;
  }

  atLeast$(version: string): Observable<boolean> {
    return this.appService.info.data$.pipe(
      map((appInfo) => this.compare(version, appInfo.buildInfo.C5ProvisioningVersion) < 1)
    );
  }

  private compare(a: string | C5Version, b: string | C5Version): number {
    if (typeof a === 'string') {
      a = this.parseVersion(a);
    }

    if (typeof b === 'string') {
      b = this.parseVersion(b);
    }

    if (a === b) {
      return 0;
    }

    let comparison = a.major - b.major;

    if (comparison === 0) {
      comparison = a.minor - b.minor;
    }

    if (comparison === 0) {
      comparison = a.patch - b.patch;
    }

    if (comparison === 0) {
      comparison = a.revision - b.revision;
    }

    return comparison;
  }

  private parseVersion(rawVersion: string): C5Version {
    const splitVersion = rawVersion.split('.');

    if (/^(\d+)(?:\.(\d+))?(?:\.(\d+))?(?:\.(\d+))?[.-]?(.*)$/.test(rawVersion) === false) {
      throw portalError({
        cause: new Error(`Given version is invalid: ${rawVersion}`),
        id: 'c5-version-checker:parse-version',
      });
    }

    return {
      raw: rawVersion,
      major: parseInt(splitVersion[0]),
      minor: splitVersion.length > 1 ? parseInt(splitVersion[1]) : 0,
      patch: splitVersion.length > 2 ? parseInt(splitVersion[2]) : 0,
      revision: splitVersion.length > 3 ? parseInt(splitVersion[3]) : 0,
    };
  }
}

export interface C5Version {
  raw: string;
  major: number;
  minor: number;
  patch: number;
  revision: number;
}
